import * as THREE from 'three'
import { CSS2DObject } from 'three/examples/jsm/renderers/CSS2DRenderer.js'
import type { Badge } from '../types'

const STATUS_COLORS: Record<string, number> = {
  present: 0x4ade80,
  absent: 0x9ca3af,
  alerte: 0xef4444,
}

const LERP_SPEED = 3

export class OccupantRenderer {
  group: THREE.Group
  mesh: THREE.Mesh
  label: CSS2DObject
  badgeCode: string
  currentZoneId: string | null = null
  private targetPosition: THREE.Vector3 | null = null
  private path: THREE.Vector3[] = []
  private pathIndex = 0

  constructor(badge: Badge) {
    this.badgeCode = badge.badge_code
    this.group = new THREE.Group()
    this.group.name = `occupant-${badge.badge_code}`
    this.group.userData = { badgeCode: badge.badge_code, type: 'occupant', name: badge.name }

    // Sphere
    const geometry = new THREE.SphereGeometry(0.3, 16, 16)
    const material = new THREE.MeshPhongMaterial({
      color: STATUS_COLORS[badge.status] ?? STATUS_COLORS.present,
    })
    this.mesh = new THREE.Mesh(geometry, material)
    this.mesh.position.y = 0.3
    this.mesh.castShadow = true
    this.group.add(this.mesh)

    // CSS2D Label
    const div = document.createElement('div')
    div.className = 'occupant-label'
    div.textContent = `${badge.name}\n${badge.badge_code}`
    div.style.cssText = `
      font-size: 10px;
      color: #fff;
      background: rgba(0,0,0,0.7);
      padding: 2px 6px;
      border-radius: 3px;
      white-space: nowrap;
      pointer-events: none;
    `
    this.label = new CSS2DObject(div)
    this.label.position.y = 0.8
    this.group.add(this.label)
  }

  setStatus(status: string) {
    const color = STATUS_COLORS[status] ?? STATUS_COLORS.present
    ;(this.mesh.material as THREE.MeshPhongMaterial).color.setHex(color!)
  }

  moveTo(position: THREE.Vector3, path?: THREE.Vector3[]) {
    if (path && path.length > 0) {
      this.path = path
      this.pathIndex = 0
      this.targetPosition = this.path[0]!
    } else {
      this.path = []
      this.pathIndex = 0
      this.targetPosition = position
    }
  }

  setPosition(x: number, y: number, z: number) {
    this.group.position.set(x, y, z)
  }

  update(dt: number) {
    if (!this.targetPosition) return

    const current = this.group.position
    const diff = new THREE.Vector3().subVectors(this.targetPosition, current)
    const dist = diff.length()

    if (dist < 0.05) {
      current.copy(this.targetPosition)
      // Move to next path point
      if (this.path.length > 0 && this.pathIndex < this.path.length - 1) {
        this.pathIndex++
        this.targetPosition = this.path[this.pathIndex]!
      } else {
        this.targetPosition = null
        this.path = []
      }
    } else {
      const step = Math.min(LERP_SPEED * dt, 1)
      current.lerp(this.targetPosition, step)
    }
  }

  setVisible(visible: boolean) {
    this.group.visible = visible
  }

  dispose() {
    this.mesh.geometry.dispose()
    ;(this.mesh.material as THREE.Material).dispose()
    this.label.element.remove()
  }
}
